// Scale bridge dashboard - everything recorded for one weighing.
//
// ─── The raw label lines are NOT shown here ────────────────────────────
// They were, as a transcript of what the printer sent. Removed on request: the
// drawer is read during a shift by somebody checking a weight, and half its
// height was a wall of TSPL nobody at the counter reads. The lines are still
// captured and still on the record - `rawLines` is untouched on the API and in
// types.ts - so nothing has been thrown away and the section can come back.
//
// ─── Why there is a summary block above the field list ─────────────────
// The drawer opens because somebody wants to check ONE weighing, and nine times
// out of ten the thing they want is the net weight and whether the test passed.
// Those were the first row of a twelve-row table, at the same size and weight as
// "Date on label" - so the answer to the question the drawer was opened to answer
// had to be found among eleven things nobody asked. It leads now, and the
// field-by-field record follows for when the summary is not enough.

import { Button, Descriptions, Drawer, Empty, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import type { DescriptionsProps } from 'antd';

import StatusTag from './StatusTag';
import Value from './Value';
import { relative, timestamp, weightKg } from '../lib/format';
import { billUrl, type Session } from '../api/client';
import { BRAND } from '../theme';
import type { Reading, ReadingItem } from '../types';

const { Text } = Typography;

type Field = NonNullable<DescriptionsProps['items']>[number];

/** Drops the rows the machine did not send. `null` in, nothing out. */
function present(fields: Array<Field | null>): Field[] {
    return fields.filter((f): f is Field => f !== null);
}

/** A drawer section: a small heading, and the thing it heads. */
function Section({ title, extra, children }: { title: string; extra?: React.ReactNode; children: React.ReactNode }) {
    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    marginBottom: 10,
                }}
            >
                <span className="sb-section-label">{title}</span>
                {extra}
            </div>
            {children}
        </div>
    );
}

export default function ReadingDetailDrawer({
    reading,
    session,
    onClose,
}: {
    reading: Reading | null;
    session: Session;
    onClose: () => void;
}) {
    // Built as data rather than as markup so the "only when the machine sent it"
    // rule is one filter at the end instead of a conditional around every row.
    // An absent field reads as absent; it never renders as an empty value that
    // looks like a parse failure.
    const labelFields: Field[] = reading
        ? present([
              reading.itemName
                  ? { key: 'item', label: 'Item', children: <Value value={reading.itemName} /> }
                  : null,
              reading.billNo
                  ? { key: 'bill', label: 'Bill no.', children: <Value value={reading.billNo} mono /> }
                  : null,
              typeof reading.grossKg === 'number'
                  ? {
                        key: 'gross',
                        label: 'Gross weight',
                        children: <span className="num">{weightKg(reading.grossKg)}</span>,
                    }
                  : null,
              typeof reading.tareKg === 'number'
                  ? {
                        key: 'tare',
                        label: 'Tare weight',
                        children: <span className="num">{weightKg(reading.tareKg)}</span>,
                    }
                  : null,
              typeof reading.price === 'number'
                  ? {
                        key: 'price',
                        label: 'Price',
                        children: <span className="num">{reading.price.toFixed(2)}</span>,
                    }
                  : null,
              reading.billNo || typeof reading.amount === 'number'
                  ? {
                        key: 'amount',
                        label: 'Amount',
                        children:
                            typeof reading.amount === 'number' ? (
                                <span className="num">{reading.amount.toFixed(2)}</span>
                            ) : (
                                // Never shown as 0.00 - the machine printed a blank AMT,
                                // meaning it calculated nothing.
                                <Text type="secondary">not calculated</Text>
                            ),
                    }
                  : null,
              reading.batchNo
                  ? { key: 'batch', label: 'Batch no.', children: <Value value={reading.batchNo} /> }
                  : null,
              { key: 'date', label: 'Date on label', children: <Value value={reading.date} /> },
              { key: 'barcode', label: 'Barcode', children: <Value value={reading.barcode} mono /> },
          ])
        : [];

    const captureFields: Field[] = reading
        ? present([
              {
                  key: 'received',
                  label: 'Received',
                  children: (
                      <Tooltip title={relative(reading.receivedAt)}>
                          <span className="num">{timestamp(reading.receivedAt)}</span>
                      </Tooltip>
                  ),
              },
              {
                  key: 'device',
                  label: 'Device',
                  children: <Value value={reading.deviceId} mono />,
              },
              ...(reading.jobSeq !== null
                  ? [
                        {
                            key: 'job',
                            label: 'Print job',
                            children: <span className="num">{reading.jobSeq}</span>,
                        },
                    ]
                  : []),
              {
                  key: 'id',
                  label: 'Record id',
                  children: <Value value={reading.id} mono />,
              },
          ])
        : [];

    return (
        <Drawer
            title={
                <div style={{ lineHeight: 1.3 }}>
                    <div style={{ fontSize: 16, fontWeight: 650 }}>Weighing</div>
                    {/* The id is a lookup key, not a heading. It goes underneath at
                        the size a reference gets, instead of setting a 30-character
                        hash in the title and pushing the actions off the edge. */}
                    {reading && (
                        <div className="mono" style={{ fontSize: 12, color: BRAND.muted }}>
                            {reading.id}
                        </div>
                    )}
                </div>
            }
            width={660}
            open={reading !== null}
            onClose={onClose}
            extra={
                reading && (
                    <Button
                        icon={<ExportOutlined />}
                        href={billUrl(session, reading.id)}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Open bill
                    </Button>
                )
            }
        >
            {reading && (
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    {/* ─── What the drawer was opened to answer ───────────── */}
                    <div className="sb-summary">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Total net weight
                        </Text>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 16,
                                flexWrap: 'wrap',
                                marginTop: 2,
                            }}
                        >
                            <span className="sb-summary__value num">
                                {weightKg(reading.totalNetWeightKg)}
                            </span>
                            <StatusTag value={reading.testing} />
                        </div>

                        {/* When the number could not be parsed, the text the label
                            carried is the only evidence there is. */}
                        {reading.totalNetWeightKg === null && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="danger" style={{ fontSize: 13 }}>
                                    Not parsed as a number — the label said{' '}
                                    <Text code className="mono">
                                        {reading.totalNetWeightRaw ?? ''}
                                    </Text>
                                </Text>
                            </div>
                        )}

                        <div style={{ marginTop: 10 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {timestamp(reading.receivedAt)} · {relative(reading.receivedAt)}
                            </Text>
                        </div>
                    </div>

                    <Section title="From the label">
                        <Descriptions
                            // ─── One column, and a fixed label width ──────────
                            // Two columns read tighter on paper and broke in
                            // practice: at 660px the drawer left each label about
                            // 90px, so "Gross weight" wrapped onto two lines and
                            // an item called APPLE was split across rows as
                            // "APPL / E". Worse, every row sized its own label
                            // cell, so the labels did not line up down the column
                            // - which is the one thing a label column is for.
                            column={1}
                            size="small"
                            bordered
                            styles={{ label: { width: 180 } }}
                            items={labelFields}
                        />
                    </Section>

                    <Section
                        title="Items"
                        extra={
                            reading.itemsTruncated && (
                                <Tooltip title="The label carried more item rows than the device stores, so this list is incomplete.">
                                    <Tag color="error" style={{ marginInlineEnd: 0 }}>
                                        Truncated
                                    </Tag>
                                </Tooltip>
                            )
                        }
                    >
                        <Table<ReadingItem>
                            size="small"
                            pagination={false}
                            rowKey={(_, i) => String(i)}
                            dataSource={reading.items}
                            locale={{
                                emptyText: (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="No item rows on this label"
                                    />
                                ),
                            }}
                            columns={[
                                {
                                    title: 'Sr No.',
                                    dataIndex: 'srNo',
                                    width: 80,
                                    render: (v: string) => <Value value={v} num />,
                                },
                                {
                                    title: 'SWG',
                                    dataIndex: 'swg',
                                    // The runs of spaces are the alignment of the gauge
                                    // readings on the paper label, so they are preserved
                                    // rather than collapsed by HTML.
                                    render: (v: string) => (
                                        <span className="preserve-spacing">{v?.trim() ? v : '—'}</span>
                                    ),
                                },
                                {
                                    title: 'N.W. (kg)',
                                    dataIndex: 'netWeight',
                                    width: 110,
                                    align: 'right',
                                    // Shown exactly as printed, leading zeros included, so
                                    // this agrees with the paper label character for
                                    // character.
                                    render: (v: string) => <Value value={v} mono num />,
                                },
                            ]}
                        />
                    </Section>

                    <Section title="How it was captured">
                        <Descriptions
                            column={1}
                            size="small"
                            bordered
                            styles={{ label: { width: 180 } }}
                            items={captureFields}
                        />
                    </Section>
                </Space>
            )}
        </Drawer>
    );
}
